'use client';

import { track } from '@vercel/analytics';
import classnames from 'classnames';
import Link from 'next/link';
import { useLocale } from 'next-intl';
import React, { useEffect, useMemo, useState } from 'react';

import { WEBAPP_URL } from '@cineamo/api/constants/http-constants';

import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';
import { useIsMobile } from '@cineamo/ui/src/helpers/screen-type';

import { useInfoBanner } from '@cineamo/ui/src/banner/useInfoBanner';
import { IconClose24 } from '@cineamo/ui/src/icons/ic-close';
import ImageWithTransition from '@cineamo/ui/src/image/ImageWithTransition';

import { addUtmParams } from '../helpers/utm-helper';

const BannerDownloadApp: React.FC<{
    id: string;
    isClosable?: boolean;
    customImage?: {
        desktop?: string;
        mobile?: string;
    };
}> = ({ id, isClosable = true, customImage }) => {
    const bannerId = `download_app_${id}`;
    const [isMounted, setIsMounted] = useState<boolean>(false);
    const locale = useLocale();
    const isMobile = useIsMobile('md');
    const bannerImage = useMemo(() => {
        const size = isMobile ? 'mobile' : 'desktop';
        const language = locale.toLowerCase() === 'de' ? 'DE' : 'EN';
        const defaultImage = loadAsset(`/images/banners/banner_cineamo_download_app_${size}_${language}.png`);
        return customImage?.[size] || defaultImage;
    }, [locale, isMobile, customImage]);
    const { isClosed, close } = useInfoBanner(bannerId);
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: hydration gate so banner only renders on the client
        setIsMounted(true);
    }, []);
    if (!isMounted || isClosed) {
        return null;
    }
    return (
        <div className="relative w-full">
            <Link
                href={addUtmParams(`${WEBAPP_URL}/download-app`, {
                    utmSource: 'web_app',
                    utmContent: 'banner-download_app',
                    utmCampaign: id,
                    utmMedium: 'website'
                })}
                onClick={() => {
                    track('Download App Banner Clicked', { bannerId: bannerId, locale, isMobile });
                }}>
                <ImageWithTransition
                    className="w-full rounded-[6px] overflow-clip"
                    src={bannerImage}
                    alt="Download Cineamo App Banner"
                    width={1024}
                    unoptimized
                    height={136}
                />
            </Link>
            {isClosable && (
                <button
                    className="absolute p-[4px] md:p-[6px] right-0 top-0 box-content"
                    onClick={(e) => {
                        e.stopPropagation();
                        track('Download App Banner Closed', { bannerId: bannerId, locale, isMobile });
                        close();
                    }}
                    aria-label={'Close Download App Banner'}>
                    <IconClose24
                        className={classnames('size-[12px] md:size-[18px] text-white opacity-50 clickable-element')}
                    />
                </button>
            )}
        </div>
    );
};

export default BannerDownloadApp;
